import { Maybe, ResponseErrorType } from '@/types/sharedTypes';
import { ReactNode, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PROXY_ASSIGN_RECIPIENT_NOTIFICATIONS_MAP,
  ProxyAssignUserStatusCodeType,
} from '@/bundle/_Recipient/_Details/ui/ProxyAssignRecipientAutocomplete/const';
import { EMAIL } from '@/const/regExpPatterns';
import { NewUserNotification } from '@/bundle/shared/components/UserAssignmentNotifications';
import { LabelSlotWrapper } from '@/components/Autocomplete/styles';
import { UserEmailInput } from '@/bundle/shared/components/SearchUserEmailInput/ui/UserEmailInput';
import { getSearchUserId } from '@/bundle/shared/components/SearchUserEmailInput/SearchUserEmailInput';
import { searchUsersByProxy } from '@/bundle/_Recipient/_Details/ui/ProxyAssignRecipientAutocomplete/api/api';

type ProxySearchUserEmailInputType = {
  wireId: string;
  assignmentStatus: ProxyAssignUserStatusCodeType;
  email: string;
  onSearch: (userId: null | string) => void;
  onSubmit: () => void;
  error?: string;
  apiError?: ResponseErrorType;
  labelSlot?: ReactNode;
};

export const ProxySearchUserEmailInput = ({
  wireId,
  email,
  onSearch,
  onSubmit,
  assignmentStatus,
  labelSlot,
  apiError,
  error,
}: ProxySearchUserEmailInputType) => {
  const [notification, setNotification] = useState<Maybe<ReactNode>>(null);
  const shouldLoadUsers = EMAIL.test(email);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['search_user_by_proxy', wireId, email],
    queryFn: () => {
      return searchUsersByProxy(wireId, email);
    },
    enabled: false,
    gcTime: 0,
  });

  const getAssignNotification = (statusCode: ProxyAssignUserStatusCodeType) => {
    if (!data?.body) return;

    const userId = !!getSearchUserId(data?.body?.results) && EMAIL.test(email);

    if (!userId) {
      return <NewUserNotification />;
    }

    return PROXY_ASSIGN_RECIPIENT_NOTIFICATIONS_MAP[statusCode];
  };

  // TODO: think about how to catch only case when request is sent
  useEffect(() => {
    if (!data?.body) {
      setNotification(null);

      return;
    }

    const userId = getSearchUserId(data?.body?.results);
    const assignNotification = getAssignNotification(assignmentStatus);

    setNotification(assignNotification);
    onSearch(userId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.body?.results, assignmentStatus]);

  const checkEmail = () => {
    onSubmit();

    if (shouldLoadUsers) {
      refetch();
    }
  };

  return (
    <UserEmailInput
      notification={notification}
      onSubmit={checkEmail}
      labelHint={<LabelSlotWrapper>{labelSlot}</LabelSlotWrapper>}
      isLoading={isFetching}
      error={error}
      apiError={apiError}
    />
  );
};
